import type { CacheLayer } from '@rstore/shared';
import type { MaybeRefOrGetter } from 'vue';
import { tryOnScopeDispose } from '@vueuse/core';
import { toValue } from 'vue';
import { useStore } from '#imports';

export interface UseOptimisticLayerOptions {
	collectionName: MaybeRefOrGetter<string | undefined>;
	itemKey: MaybeRefOrGetter<string | number | undefined>;
}

export function useOptimisticLayer(options: UseOptimisticLayerOptions) {
	const store = useStore();
	const layerId = `optimistic:${crypto.randomUUID()}`;

	let pendingState: Record<string, any> = {};
	let active = false;

	function update(data: Record<string, any>) {
		const name = toValue(options.collectionName);
		const key = toValue(options.itemKey);
		if (!name || key == null) return;

		pendingState = { ...pendingState, ...data };

		const layer: CacheLayer = {
			id: layerId,
			collectionName: name,
			state: { [key]: { ...pendingState } },
			deletedItems: new Set(),
			optimistic: true,
		};

		// addLayer internally removes existing layer with same ID before adding
		store.$cache.addLayer(layer);
		active = true;
	}

	function clear() {
		if (!active) return;
		store.$cache.removeLayer(layerId);
		pendingState = {};
		active = false;
	}

	// commit and rollback are semantic aliases — both remove the layer
	const commit = clear;
	const rollback = clear;

	tryOnScopeDispose(clear);

	return { update, commit, rollback };
}
